import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { CalendarDays, Users, Send, Heart, Briefcase, Sparkles, Check, Home } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const eventCategories = [
  {
    icon: Home,
    title: "Séjours",
    types: [
      { value: "sejour_simple", label: "Séjour simple (famille / amis)" },
    ]
  },
  {
    icon: Heart,
    title: "Événements privés",
    types: [
      { value: "mariage", label: "Mariage" },
      { value: "anniversaire", label: "Anniversaire" },
      { value: "evjf_evg", label: "EVJF / EVG" },
      { value: "reunion_famille", label: "Réunion de famille" },
      { value: "fetes", label: "Fêtes (Nouvel an, Noël...)" },
    ]
  },
  {
    icon: Briefcase,
    title: "Événements professionnels",
    types: [
      { value: "seminaire", label: "Séminaire" },
      { value: "team_building", label: "Team building" },
      { value: "conference", label: "Conférence" },
      { value: "shooting", label: "Shooting / Tournage" },
      { value: "retraite_entreprise", label: "Retraite d'entreprise" },
    ]
  },
  {
    icon: Sparkles,
    title: "Bien-être",
    types: [
      { value: "yoga", label: "Retraite yoga" },
      { value: "bien_etre", label: "Séjour bien-être" },
      { value: "atelier", label: "Atelier / Stage" },
      { value: "coaching", label: "Coaching / Développement personnel" },
      { value: "autre", label: "Autre" },
    ]
  }
];

const allEventTypes = eventCategories.flatMap(cat => cat.types);

const EventsPage = () => {
  const [formData, setFormData] = useState({
    event_type: "",
    guests: "",
    check_in: undefined,
    check_out: undefined,
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [siteSettings, setSiteSettings] = useState({ pool_under_construction: true });

  // Fetch site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings/public`);
        setSiteSettings(res.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.event_type || !formData.guests || !formData.check_in || !formData.check_out || !formData.name || !formData.email || !formData.phone) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/quotes/request`, {
        ...formData,
        guests: parseInt(formData.guests),
        check_in: format(formData.check_in, 'yyyy-MM-dd'),
        check_out: format(formData.check_out, 'yyyy-MM-dd')
      });
      setSubmitted(true);
      toast.success('Demande envoyée avec succès !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[#FAFAFA]" data-testid="quote-success">
        <div className="max-w-md mx-auto text-center p-12 bg-white border border-[#E5E5E5]">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-['Playfair_Display'] mb-4">Demande envoyée !</h1>
          <p className="text-[#666666] mb-8 font-['Outfit']">
            Nous avons bien reçu votre demande de devis. Notre équipe vous contactera sous 48h.
          </p>
          <Button 
            onClick={() => setSubmitted(false)}
            variant="outline"
            className="border-[#0A0A0A]"
            data-testid="new-quote-btn"
          >
            Nouvelle demande
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#FAFAFA]" data-testid="events-page">
      {/* Hero */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img 
          src="https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/nzuij2f6_IMG_1021.JPG"
          alt="Événement au domaine"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-center">
          <div className="text-white px-6">
            <span className="text-sm uppercase tracking-[0.3em] mb-4 block opacity-80 font-['Outfit']">
              Groupes & Événements
            </span>
            <h1 className="text-4xl md:text-6xl font-['Playfair_Display'] mb-6">
              Un lieu d'exception pour vos événements
            </h1>
            <p className="text-xl opacity-90 max-w-2xl mx-auto font-['Outfit']">
              Plus de 12 personnes ? Recevez un devis personnalisé.
            </p>
          </div>
        </div>
      </div>

      {/* Event Types */}
      <section className="section-padding bg-white" data-testid="event-types-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4 block font-['Outfit']">
              Nos offres
            </span>
            <h2 className="text-4xl font-['Playfair_Display'] text-[#0A0A0A]">
              Quel événement souhaitez-vous organiser ?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {eventCategories.map((category, index) => (
              <div key={index} className="bg-[#FAFAFA] p-6 border border-[#E5E5E5]" data-testid={`event-category-${index}`}>
                <category.icon className="w-10 h-10 text-[#C5A059] mb-4" />
                <h3 className="text-lg font-['Playfair_Display'] mb-3 text-[#0A0A0A]">{category.title}</h3>
                <ul className="space-y-1">
                  {category.types.map((type, i) => (
                    <li key={i} className="text-[#666666] font-['Outfit'] text-sm">• {type.label}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="section-padding bg-[#FAFAFA]" data-testid="quote-form-section">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4 block font-['Outfit']">
              Devis personnalisé
            </span>
            <h2 className="text-4xl font-['Playfair_Display'] text-[#0A0A0A]">
              Demandez votre devis
            </h2>
            <p className="text-[#C5A059] mt-2 font-['Outfit'] font-medium">
              Location de la villa en toute liberté, avec accompagnement possible sur demande.
            </p>
            <p className="text-[#666666] mt-4 font-['Outfit']">
              Remplissez le formulaire ci-dessous, nous vous répondrons sous 48h.
            </p>
            <p className="text-[#0A0A0A] mt-4 font-['Outfit'] text-sm bg-[#F5F5F5] p-4 rounded border-l-4 border-[#C5A059]">
              La demande de devis concerne la mise à disposition de la villa. Vous organisez votre séjour en toute liberté. 
              Nous pouvons, si vous le souhaitez, vous accompagner sur certains besoins (équipements, aménagements, prestataires, etc.).
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white border border-[#E5E5E5] p-8 md:p-12" data-testid="quote-form">
            {/* Pool notice */}
            {siteSettings.pool_under_construction && (
              <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded text-sm" data-testid="pool-notice">
                <p className="text-amber-800 font-['Outfit']">
                  <strong>ℹ️ Information :</strong> La piscine est actuellement en travaux et sera bientôt disponible.
                </p>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Event Type */}
              <div className="md:col-span-2">
                <Label className="font-['Outfit'] mb-2 block">Motif de l'événement *</Label>
                <Select 
                  value={formData.event_type} 
                  onValueChange={(value) => setFormData({...formData, event_type: value})}
                >
                  <SelectTrigger data-testid="event-type-select">
                    <SelectValue placeholder="Sélectionnez un motif" />
                  </SelectTrigger>
                  <SelectContent>
                    {eventCategories.map((category) => (
                      <React.Fragment key={category.title}>
                        <div className="px-2 py-1 text-xs text-[#666666] font-semibold uppercase">{category.title}</div>
                        {category.types.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Guests */}
              <div>
                <Label className="font-['Outfit'] mb-2 block">Nombre de personnes *</Label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                  <Input 
                    type="number"
                    min="1"
                    value={formData.guests}
                    onChange={(e) => setFormData({...formData, guests: e.target.value})}
                    className="pl-10"
                    placeholder="Ex: 25"
                    data-testid="guests-input"
                  />
                </div>
              </div>

              {/* Dates */}
              <div>
                <Label className="font-['Outfit'] mb-2 block">Dates souhaitées *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-left font-normal"
                      data-testid="date-picker-trigger"
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {formData.check_in && formData.check_out ? (
                        `${format(formData.check_in, 'd MMM', { locale: fr })} - ${format(formData.check_out, 'd MMM yyyy', { locale: fr })}`
                      ) : (
                        "Sélectionnez les dates"
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={{ from: formData.check_in, to: formData.check_out }}
                      onSelect={(range) => setFormData({...formData, check_in: range?.from, check_out: range?.to})}
                      locale={fr}
                      numberOfMonths={2}
                      data-testid="date-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Name */}
              <div>
                <Label className="font-['Outfit'] mb-2 block">Nom complet *</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Jean Dupont"
                  data-testid="name-input"
                />
              </div>

              {/* Email */}
              <div>
                <Label className="font-['Outfit'] mb-2 block">Email *</Label>
                <Input 
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="jean@exemple.com"
                  data-testid="email-input"
                />
              </div>

              {/* Phone */}
              <div className="md:col-span-2">
                <Label className="font-['Outfit'] mb-2 block">Téléphone *</Label>
                <Input 
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="06 12 34 56 78"
                  data-testid="phone-input"
                />
              </div>

              {/* Message */}
              <div className="md:col-span-2">
                <Label className="font-['Outfit'] mb-2 block">Décrivez votre projet</Label>
                <textarea 
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full border border-[#E5E5E5] p-4 h-32 resize-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]"
                  placeholder="Précisez si vous avez besoin d'équipements ou de recommandations (mobilier, aménagements, prestataires, etc.)"
                  data-testid="message-input"
                />
              </div>
            </div>

            <Button 
              type="submit"
              className="w-full mt-8 bg-[#C5A059] hover:bg-[#A6864A] text-white py-6"
              disabled={loading}
              data-testid="submit-quote-btn"
            >
              {loading ? (
                'Envoi en cours...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer ma demande
                </>
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
